import React, { useState } from 'react';
import SEO from '../../components/SEO';
import { generateArticle } from '../../utils/structuredData';
import seoData from '../../config/seoData';
import { MessageCircle, Instagram, Menu, X, ArrowLeft, Calendar, Clock as ClockIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useNavigate, Link } from 'react-router-dom';

const TempoLimpeza = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent('Olá! Gostaria de saber mais sobre prazos de limpeza pós-obra.');
    window.open(`https://wa.me/5531971823249?text=${message}`, '_blank');
  };

  return (
    <div className="min-h-screen
      <SEO 
        title={seoData['/blog/tempo-limpeza-pos-obra'].title}
        description={seoData['/blog/tempo-limpeza-pos-obra'].description}
        canonical={seoData['/blog/tempo-limpeza-pos-obra'].canonical}
      /> bg-white">
      <header className="bg-slate-900 text-white py-4 px-6 sticky top-0 z-40 shadow-lg">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => navigate('/')}>
            <img 
              src="https://customer-assets.emergentagent.com/job_locacao-pisos/artifacts/9nveujmi_file_00000000e2f071f5a40c0fc04e1c9bed.png" 
              alt="WellTec Logo" 
              className="h-16 w-auto"
            />
          </div>
          
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="hover:text-blue-400 transition-colors">Início</Link>
            <Link to="/servicos" className="hover:text-blue-400 transition-colors">Serviços</Link>
            <Link to="/limpeza-pos-obra" className="hover:text-green-400 transition-colors">Limpeza Pós-Obra</Link>
            <Link to="/locacao-lavadora-piso" className="hover:text-blue-400 transition-colors">Locação</Link>
            <Link to="/blog" className="text-green-400 font-semibold">Blog</Link>
            <Button onClick={handleWhatsAppClick} className="bg-green-500 hover:bg-green-600">
              <MessageCircle className="w-4 h-4 mr-2" />WhatsApp
            </Button>
          </div>

          <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden absolute top-full left-0 right-0 bg-slate-900 border-t border-slate-700 shadow-xl">
            <div className="flex flex-col space-y-1 p-4">
              <button onClick={() => { navigate('/'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded">Início</button>
              <button
                onClick={() => { navigate('/servicos'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Serviços
              </button>
              <button
                onClick={() => { navigate('/limpeza-pos-obra'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors text-green-400">
                Limpeza Pós-Obra
              </button>
              <button
                onClick={() => { navigate('/locacao-lavadora-piso'); setMobileMenuOpen(false); }}
                className="text-left py-3 px-4 hover:bg-slate-800 rounded transition-colors">
                Locação
              </button>
              <button onClick={() => { navigate('/blog'); setMobileMenuOpen(false); }} className="text-left py-3 px-4 hover:bg-slate-800 rounded text-green-400 font-semibold">Blog</button>
            </div>
          </div>
        )}
      </header>

      <article className="py-12 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <button onClick={() => navigate('/blog')} className="flex items-center text-blue-600 hover:text-blue-800 mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />Voltar para o Blog
          </button>

          <header className="mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Quanto Tempo Leva uma Limpeza Pós-Obra Profissional?
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-gray-600 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" /><span>Janeiro 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <ClockIcon className="w-4 h-4" /><span>7 min de leitura</span>
              </div>
            </div>
          </header>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-6">
              Uma das perguntas mais frequentes sobre limpeza pós-obra é: <strong>"quanto tempo vai levar?"</strong> A resposta depende de vários fatores. Veja o guia completo de prazos.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Fatores Que Influenciam o Tempo
            </h2>

            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li><strong>Tamanho da área</strong> (m²)</li>
              <li><strong>Nível de sujeira</strong> (leve, médio, pesado)</li>
              <li><strong>Tipo de piso</strong> e revestimentos</li>
              <li><strong>Número de ambientes</strong></li>
              <li><strong>Equipamentos disponíveis</strong></li>
              <li><strong>Tamanho da equipe</strong></li>
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Tabela de Tempo: Residencial
            </h2>

            <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden mb-6">
              <table className="w-full">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="p-4 text-left">Tipo de Imóvel</th>
                    <th className="p-4 text-left">Área Média</th>
                    <th className="p-4 text-left">Equipe</th>
                    <th className="p-4 text-left">Tempo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Apt 1 quarto</td>
                    <td className="p-4">40-50m²</td>
                    <td className="p-4">2 pessoas</td>
                    <td className="p-4 text-green-600 font-semibold">4-6 horas</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Apt 2 quartos</td>
                    <td className="p-4">60-80m²</td>
                    <td className="p-4">2 pessoas</td>
                    <td className="p-4 text-green-600 font-semibold">6-8 horas</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Apt 3 quartos</td>
                    <td className="p-4">90-120m²</td>
                    <td className="p-4">2-3 pessoas</td>
                    <td className="p-4 text-green-600 font-semibold">8-12 horas</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Casa 3 quartos</td>
                    <td className="p-4">150-200m²</td>
                    <td className="p-4">3 pessoas</td>
                    <td className="p-4 text-green-600 font-semibold">1-2 dias</td>
                  </tr>
                  <tr>
                    <td className="p-4 font-semibold">Sobrado 4 quartos</td>
                    <td className="p-4">250-350m²</td>
                    <td className="p-4">3-4 pessoas</td>
                    <td className="p-4 text-green-600 font-semibold">2-3 dias</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Tabela de Tempo: Comercial e Industrial
            </h2>

            <div className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden mb-6">
              <table className="w-full">
                <thead className="bg-slate-900 text-white">
                  <tr>
                    <th className="p-4 text-left">Tipo</th>
                    <th className="p-4 text-left">Área</th>
                    <th className="p-4 text-left">Tempo</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Loja pequena</td>
                    <td className="p-4">50-100m²</td>
                    <td className="p-4 text-green-600 font-semibold">1-2 dias</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Escritório</td>
                    <td className="p-4">200-500m²</td>
                    <td className="p-4 text-green-600 font-semibold">2-3 dias</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Galpão pequeno</td>
                    <td className="p-4">500m²</td>
                    <td className="p-4 text-green-600 font-semibold">1-2 dias</td>
                  </tr>
                  <tr className="border-b">
                    <td className="p-4 font-semibold">Galpão médio</td>
                    <td className="p-4">1.000m²</td>
                    <td className="p-4 text-green-600 font-semibold">2-3 dias</td>
                  </tr>
                  <tr>
                    <td className="p-4 font-semibold">Galpão grande</td>
                    <td className="p-4">2.000m²+</td>
                    <td className="p-4 text-green-600 font-semibold">3-5 dias</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Comparativo: Com e Sem Equipamentos Profissionais
            </h2>

            <Card className="bg-blue-50 border-2 border-blue-200 mb-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-4">Exemplo: Apartamento 80m² (2 quartos)</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Limpeza Manual (sem equipamentos)</h4>
                    <p className="text-gray-700">⏱️ Tempo: 2-3 dias (16-24 horas de trabalho)</p>
                    <p className="text-gray-700">👥 Equipe: 2 pessoas</p>
                  </div>

                  <div>
                    <h4 className="font-semibold text-green-700 mb-2">Limpeza Profissional (com equipamentos)</h4>
                    <p className="text-gray-700">⏱️ Tempo: 6-8 horas</p>
                    <p className="text-gray-700">👥 Equipe: 2 pessoas</p>
                    <p className="text-green-700 font-semibold mt-2">✓ 70% mais rápido!</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              O Que Pode Aumentar o Tempo?
            </h2>

            <ul className="list-disc list-inside text-gray-700 space-y-2 mb-6">
              <li><strong>Sujeira muito pesada:</strong> +30-50% de tempo</li>
              <li><strong>Materiais delicados:</strong> Mármore, granito exigem mais cuidado</li>
              <li><strong>Muitos ambientes pequenos:</strong> Mais tempo em cantos e detalhes</li>
              <li><strong>Acesso difícil:</strong> Prédios sem elevador de serviço</li>
              <li><strong>Retrabalho:</strong> Se obra não finalizou completamente</li>
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-10 mb-4">
              Como Reduzir o Tempo de Limpeza?
            </h2>

            <ol className="list-decimal list-inside text-gray-700 space-y-2 mb-6">
              <li>Finalize TODA a obra antes de chamar a limpeza</li>
              <li>Remova o entulho grosso você mesmo</li>
              <li>Contrate empresa com equipamentos profissionais</li>
              <li>Agende com antecedência (evite urgências)</li>
              <li>Libere acesso facilitado (chaves, elevadores)</li>
            </ol>

            <Card className="bg-green-50 border-2 border-green-200 mb-6">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-3">💡 Dica Importante</h3>
                <p className="text-gray-700">
                  Reserve 1 dia a mais no seu cronograma para imprevistos. É melhor sobrar tempo do que atrasar uma mudança ou inauguração.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-blue-900 text-white mb-6">
              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold mb-4">Precisa de Limpeza Rápida e Eficiente?</h3>
                <p className="mb-6">
                  A WellTec possui equipamentos profissionais para reduzir o tempo de limpeza
                </p>
                <Button 
                  onClick={handleWhatsAppClick}
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-6"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Solicitar Orçamento com Prazo
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </article>

      <footer className="bg-slate-900 text-white py-12 mt-12">
        <div className="container mx-auto px-6">
          <div className="text-center text-gray-400 text-sm">
            <p>&copy; {new Date().getFullYear()} WellTec. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>

      <button onClick={handleWhatsAppClick} className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl hover:scale-110 transition-all z-50">
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
};

export default TempoLimpeza;
